import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { UsersQueryApi } from '@lib/api/endpoints';
import { useAuth } from '@lib/auth';
import { isPostHogLoaded, onPostHogLoaded, posthog } from '@lib/posthog.lib';
import type { RootState } from '@lib/redux.lib/store';
import type { EverysetUser } from '@lib/types/user.types';

import {
  isClarityTracking,
  markClarityAgeVerified,
  setClarityTag,
  startClarityTracking,
  stopClarityTracking,
  stopClarityTrackingForOtherUser,
} from './clarityClient';
import {
  isUserProfileForUser,
  resolveAgeSignalsFromUserProfile,
  resolveClaritySessionIdentity,
} from './claritySessionUser';
import { isAdultFromBirthday } from './isAdultFromBirthday';

const clarityProjectId = process.env.NEXT_PUBLIC_CLARITY_PROJECT_ID;
const isProduction = process.env.NEXT_PUBLIC_NODE_ENV === 'production';
const isClarityEnabled = isProduction && Boolean(clarityProjectId);

const syncSessionTags = (
  userId: number,
  impersonatedUserId: number | null,
  email: string | null
): void => {
  setClarityTag('user_id', String(userId));
  setClarityTag(
    'impersonated_target_id',
    impersonatedUserId != null ? String(impersonatedUserId) : ''
  );

  setClarityTag('email', email ?? '');

  const posthogId = posthog.get_distinct_id?.();

  setClarityTag('posthog_id', posthogId ? String(posthogId) : '');
};

type AgeVerification = 'adult' | 'restricted';

/**
 * Fail-open: restrict only when a valid birthday proves age under 18, or when
 * birthday is absent/invalid and `is_minor` is explicitly true.
 */
const verifyAgeFromProfile = (
  profileInfo: UserProfile | null | undefined
): AgeVerification => {
  const { birthday, isMinor } = resolveAgeSignalsFromUserProfile(profileInfo);

  return isAdultFromBirthday(birthday, isMinor) ? 'adult' : 'restricted';
};

/**
 * Fallback when UserProfile lookup fails: use Redux `EverysetUser.is_minor`
 * only when that profile belongs to the session user. Unknown / absent
 * `is_minor` stays fail-open (adult).
 */
const verifyAgeFromSessionUser = (
  userId: number,
  profile: EverysetUser | null | undefined
): AgeVerification => {
  if (profile == null || Number(profile.id) !== userId) {
    return 'adult';
  }

  return isAdultFromBirthday(null, profile.is_minor) ? 'adult' : 'restricted';
};

/**
 * Resolves age for the session user after profile data is available.
 * Prefers Redux `IUserInfoDAO` (`about.birthday`, `is_minor`) when that record
 * belongs to the session user; otherwise fetches the session user's profile and
 * waits before deciding — so a minor is not recorded during the loading gap.
 * Fetch failure falls back to Redux `profile.is_minor` when available; otherwise
 * fail-open (adult) so unknown age still records.
 */
const verifyUserAge = async (
  userId: number,
  profileInfo: UserProfile | null | undefined,
  profile: EverysetUser | null | undefined
): Promise<AgeVerification> => {
  if (isUserProfileForUser(profileInfo, userId)) {
    return verifyAgeFromProfile(profileInfo);
  }

  try {
    const remoteProfile = await UsersQueryApi.getUserProfile(userId);

    return verifyAgeFromProfile(remoteProfile);
  } catch {
    return verifyAgeFromSessionUser(userId, profile);
  }
};

const applyAgeVerification = (
  verification: AgeVerification,
  userId: number
): boolean => {
  if (verification === 'restricted') {
    stopClarityTracking({ clearAgeVerification: true });

    return false;
  }

  markClarityAgeVerified(userId);

  return startClarityTracking(clarityProjectId, userId);
};

type TrackedIdentity = {
  userId: number;
  impersonatedUserId: number | null;
  email: string | null;
};

/**
 * Initializes Microsoft Clarity for authenticated users, then tags the session
 * with the session user id, optional impersonated target, session-user email,
 * and PostHog distinct id.
 *
 * Age-gating is fail-open: Clarity records by default and only stops when a
 * valid birthday proves the user is under 18, or when birthday is absent and
 * `is_minor` is explicitly true. Missing profile or birthday must not block
 * recording. Eligibility is decided only after the session-user profile fetch
 * settles, so a minor's first seconds are not recorded early.
 * Attribution follows the Cognito session (person at the keyboard). Disabled
 * outside production (same gate as PostHog). Renders nothing.
 */
export const ClarityLifecycle = (): null => {
  const { authUserSession, sessionLoading } = useAuth();
  const profile = useSelector(
    (state: RootState) => state.app.user.profile ?? null
  );
  const profileInfo = useSelector(
    (state: RootState) => state.app.user.IUserInfoDAO ?? null
  );
  const impersonatedUserId = useSelector(
    (state: RootState) => state.app.user.impersonatedUserId ?? null
  );
  const [isTracking, setIsTracking] = useState(false);
  const [trackedIdentity, setTrackedIdentity] =
    useState<TrackedIdentity | null>(null);
  const [isPostHogReady, setIsPostHogReady] = useState(isPostHogLoaded);
  const previousSessionRef =
    useRef<{
      userId: number;
      email: string | null;
    } | null>(null);

  useEffect(() => {
    if (isPostHogReady) {
      return;
    }

    return onPostHogLoaded(() => {
      setIsPostHogReady(true);
    });
  }, [isPostHogReady]);

  useEffect(() => {
    if (!isClarityEnabled) {
      return;
    }

    if (authUserSession == null) {
      stopClarityTracking({ clearAgeVerification: true });
      setIsTracking(false);
      setTrackedIdentity(null);
      previousSessionRef.current = null;

      return;
    }

    // Auth bootstrap and logout both set loading before session is cleared.
    // Stop recording immediately so teardown cannot leave an active session.
    if (sessionLoading === true) {
      stopClarityTracking();
      setIsTracking(false);

      return;
    }

    const identity = resolveClaritySessionIdentity(
      authUserSession,
      profile,
      impersonatedUserId
    );

    // Only abort when Cognito has no session user id — never because profile
    // or birthday is still undefined.
    if (identity == null) {
      stopClarityTracking();
      setIsTracking(false);
      setTrackedIdentity(null);
      previousSessionRef.current = null;

      return;
    }

    const { userId, impersonatedUserId: targetId, email } = identity;
    const previousSession = previousSessionRef.current;

    // Id or email change means another user session — restart Clarity.
    if (
      previousSession != null &&
      (previousSession.userId !== userId || previousSession.email !== email)
    ) {
      stopClarityTracking({
        clearAgeVerification: previousSession.userId !== userId,
      });
    }

    stopClarityTrackingForOtherUser(userId);
    previousSessionRef.current = { userId, email };
    setTrackedIdentity({ userId, impersonatedUserId: targetId, email });
    setIsTracking(isClarityTracking());

    let cancelled = false;

    const evaluateEligibility = async () => {
      // Awaits Redux or remote profile before the under-18 check.
      const verification = await verifyUserAge(userId, profileInfo, profile);

      if (cancelled) return;

      setIsTracking(applyAgeVerification(verification, userId));
    };

    evaluateEligibility();

    return () => {
      cancelled = true;
    };
  }, [
    sessionLoading,
    authUserSession,
    profile,
    profileInfo,
    impersonatedUserId,
  ]);

  useEffect(() => {
    if (!isClarityEnabled || !isTracking || trackedIdentity == null) return;

    // Re-run when PostHog becomes ready or the session user changes so
    // posthog_id is not left anonymous / unset after a late identify.
    syncSessionTags(
      trackedIdentity.userId,
      trackedIdentity.impersonatedUserId,
      trackedIdentity.email
    );
  }, [
    isTracking,
    trackedIdentity,
    isPostHogReady,
    profile?.id,
    profile?.email,
  ]);

  useEffect(
    () => () => {
      if (!isClarityEnabled) return;

      stopClarityTracking({ clearAgeVerification: true });
    },
    []
  );

  return null;
};
